import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { SITE, SERVICES } from "../lib/site";
import SectionHeader from "../components/SectionHeader";
import Seo from "../components/Seo";

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in your name, email and message.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/inquiries", form);
            toast.success("Thanks! We'll be in touch shortly.");
            setForm({ name: "", email: "", phone: "", service: "", message: "" });
        } catch (err) {
            toast.error("Something went wrong. Please call us instead.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <Seo
                title="Contact Dandy Builders — Wokingham, Berkshire"
                description="Call 07786 732510 or email office@dandybuilders.co.uk. Free site visits across Wokingham, Reading, Bracknell, Henley and surrounding Berkshire."
                path="/contact"
            />
            <section className="bg-muted border-b border-border">
                <div className="container-x py-20 md:py-28">
                    <SectionHeader
                        eyebrow="Contact Us"
                        title="Let's talk about your project."
                        lead="Drop us a message, give us a ring, or pop in for a chat. We'll come out, take a look, and send you a clear written quote."
                    />
                </div>
            </section>

            <section className="section-pad container-x grid md:grid-cols-12 gap-12">
                {/* Info */}
                <aside className="md:col-span-5 space-y-6">
                    <div className="card-earthy">
                        <h3 className="font-heading text-2xl font-bold">Get in touch</h3>
                        <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
                            Open Mon–Sat. Emergency call-outs available outside hours.
                        </p>
                        <ul className="mt-6 space-y-4">
                            <li className="flex gap-4">
                                <span className="h-10 w-10 grid place-items-center rounded-md bg-primary text-primary-foreground shrink-0">
                                    <Phone size={16} strokeWidth={1.75} />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Phone & WhatsApp</div>
                                    <a href={SITE.phoneLink} className="font-heading font-bold text-lg hover:text-primary" data-testid="contact-phone">{SITE.phone}</a>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <span className="h-10 w-10 grid place-items-center rounded-md bg-primary text-primary-foreground shrink-0">
                                    <Mail size={16} strokeWidth={1.75} />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</div>
                                    <a href={SITE.emailLink} className="font-heading font-bold text-lg hover:text-primary break-all" data-testid="contact-email">{SITE.email}</a>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <span className="h-10 w-10 grid place-items-center rounded-md bg-primary text-primary-foreground shrink-0">
                                    <MapPin size={16} strokeWidth={1.75} />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Service Area</div>
                                    <p className="font-medium leading-snug mt-0.5">{SITE.serviceArea}</p>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <span className="h-10 w-10 grid place-items-center rounded-md bg-secondary text-secondary-foreground shrink-0">
                                    <MessageCircle size={16} strokeWidth={1.75} />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">WhatsApp</div>
                                    <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="font-heading font-bold text-lg hover:text-secondary" data-testid="contact-whatsapp">Chat on WhatsApp</a>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <div className="card-earthy">
                        <h3 className="font-heading text-lg font-bold">Opening Hours</h3>
                        <ul className="mt-4 divide-y divide-border">
                            {SITE.hours.map((h) => (
                                <li key={h.day} className="flex justify-between py-2.5 text-sm">
                                    <span className="font-medium">{h.day}</span>
                                    <span className="text-muted-foreground">{h.time}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="rounded-lg overflow-hidden border border-border">
                        <iframe
                            title="Wokingham Map"
                            src="https://www.openstreetmap.org/export/embed.html?bbox=-0.9123%2C51.3886%2C-0.7923%2C51.4486&layer=mapnik&marker=51.4123%2C-0.8423"
                            className="w-full h-72 border-0"
                            loading="lazy"
                            data-testid="contact-map"
                        />
                    </div>
                </aside>

                {/* Form */}
                <form
                    onSubmit={submit}
                    className="md:col-span-7 card-earthy space-y-5"
                    data-testid="contact-form"
                >
                    <div>
                        <h3 className="font-heading text-2xl md:text-3xl font-extrabold">Send a message</h3>
                        <p className="text-muted-foreground mt-1.5 text-sm">We aim to reply within one working day.</p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Your name *">
                            <input value={form.name} onChange={update("name")} className="input-style" data-testid="contact-name-input" required />
                        </Field>
                        <Field label="Email *">
                            <input type="email" value={form.email} onChange={update("email")} className="input-style" data-testid="contact-email-input" required />
                        </Field>
                        <Field label="Phone">
                            <input value={form.phone} onChange={update("phone")} className="input-style" data-testid="contact-phone-input" />
                        </Field>
                        <Field label="Service">
                            <select value={form.service} onChange={update("service")} className="input-style" data-testid="contact-service-select">
                                <option value="">Select a service…</option>
                                {SERVICES.map((s) => (
                                    <option key={s.slug} value={s.name}>{s.name}</option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <Field label="Tell us about your project *">
                        <textarea rows={6} value={form.message} onChange={update("message")} className="input-style resize-none" data-testid="contact-message-input" required />
                    </Field>
                    <button type="submit" disabled={loading} className="btn-primary w-full md:w-auto disabled:opacity-60" data-testid="contact-submit-btn">
                        {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                        {loading ? "Sending…" : "Send Message"}
                    </button>
                    <p className="text-xs text-muted-foreground">By sending, you agree to be contacted regarding your enquiry. We never share your details.</p>

                    <style>{`.input-style{background:#fff;border:1px solid hsl(var(--border));border-radius:0.5rem;padding:0.75rem 1rem;width:100%;font-size:0.95rem;transition:all 0.15s;color:hsl(var(--foreground))}.input-style:focus{outline:none;border-color:hsl(var(--primary));box-shadow:0 0 0 3px hsla(155,36%,18%,0.12)}`}</style>
                </form>
            </section>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium mb-2">{label}</span>
            {children}
        </label>
    );
}
